// Vocabulary of the notification center, shared by the page, its pinned
// "Latest replies" panel and the bell dropdown. The API side is
// NotificationsLib::getFeed: one item shape for every source, community,
// email campaign or LinkedIn, so the same words and colours apply everywhere
// an item shows.

export const userIcon = `${process.env.PUBLIC_URL}/img/user-icon.png`;

export const KINDS = {
  reply: { label: 'Response', badge: 'feed-badge--reply' },
  message: { label: 'Message', badge: 'feed-badge--message' },
  visit: { label: 'Profile view', badge: 'feed-badge--visit' },
  deck: { label: 'Deck review', badge: 'feed-badge--deck' },
  download: { label: 'Deck download', badge: 'feed-badge--deck' },
  financials: { label: 'Financials review', badge: 'feed-badge--deck' },
  lead: { label: 'New lead', badge: 'feed-badge--lead' },
  ping: { label: 'Ping', badge: 'feed-badge--lead' },
  declined: { label: 'Declined', badge: 'feed-badge--declined' },
  request: { label: 'Request sent', badge: 'feed-badge--request' },
  activity: { label: 'Activity', badge: 'feed-badge--request' },
};

// The kinds the filter offers, in the order a founder wants them. Messages are
// not on the list: they are part of Response, see KIND_GROUPS below.
export const KIND_ORDER = [
  'reply', 'visit', 'deck', 'download', 'financials', 'lead', 'ping', 'declined', 'request', 'activity',
];

// An answer to a campaign email and a message in the community are the same
// event to a founder, so one filter covers both and the source toggles are
// what tell them apart. Kinds absent here filter to themselves.
export const KIND_GROUPS = {
  reply: ['reply', 'message'],
};

export const kindMatches = (item, kind) => (KIND_GROUPS[kind] || [kind]).includes(item.kind);

export const SOURCES = {
  community: 'Community',
  campaign: 'Email campaigns',
  linkedin: 'LinkedIn',
};

// What a response answered, by source, for the card title.
export const RESPONSE_TARGET = {
  campaign: 'your email',
  linkedin: 'your LinkedIn message',
};

// Category comes from the API, one value per reply (AnalyticsHelperLib::responseCategory)
export const SENTIMENTS = {
  positive: { label: 'Positive' },
  neutral: { label: 'Neutral' },
  negative: { label: 'Negative' },
  unclassified: { label: 'Not classified' },
};

export const SENTIMENT_ORDER = ['positive', 'neutral', 'negative', 'unclassified'];

// One line under a reply, chosen by that reply's own sentiment. A no on its
// own reads as failure; the line says what to do with it.
export const encouragementFor = (sentiment) => {
  switch (sentiment) {
    case 'positive':
      return 'Reply today: answers sent within a day are the ones that turn into meetings.';
    case 'neutral':
      return 'No yes yet, and that is how most conversations start. An investor who answers once will usually '
        + 'answer again, so come back with one concrete update they can react to, a new customer, a revenue '
        + 'number or a closing date.';
    case 'negative':
      return 'Hearing no is a natural part of any fundraise, it is simply how the process works, and the '
        + 'strongest rounds are built on a long list of them. Do not give up: fundraising is a numbers game. '
        + 'Keep your campaigns moving and keep adding investors who back companies like yours.';
    default:
      return null;
  }
};

// What the top bar icons say when something arrived: replies on the bell,
// messages on the mail icon. { count, text, link } or null.
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

export const unseenReplies = (byKind) => {
  const count = byKind && byKind.reply > 0 ? byKind.reply : 0;
  return count
    ? {
      count,
      kind: 'reply',
      text: plural(count, 'new response', 'new responses'),
      link: '/notifications?type=reply&new=1',
    }
    : null;
};

export const unseenMessages = (byKind) => {
  const count = byKind && byKind.message > 0 ? byKind.message : 0;
  return count
    ? {
      count,
      kind: 'message',
      text: plural(count, 'new message', 'new messages'),
      link: '/notifications?type=message&new=1',
    }
    : null;
};

// Where an avatar lives depends on who the actor is: investors and database
// contacts share one bucket, company logos another, LinkedIn connections a third.
export const avatarUrl = (actor) => {
  if (!actor || !actor.avatar) return userIcon;
  let folder = process.env.REACT_APP_AWS_INVESTORS_PATH;
  if (actor.avatarKind === 'logo') folder = process.env.REACT_APP_AWS_LOGOS_PATH;
  if (actor.avatarKind === 'linkedin') folder = process.env.REACT_APP_AWS_LINKEDININTRO_PATH;
  return process.env.REACT_APP_AWS_ROOT + folder + actor.avatar;
};

// "3 h", "1.5 days", "12 days"
export const formatHours = (hours) => {
  if (hours === null || hours === undefined) return null;
  if (hours < 1) return 'under an hour';
  if (hours < 48) return `${Math.round(hours)} h`;
  const days = hours / 24;
  return `${days < 10 ? days.toFixed(1).replace(/\.0$/, '') : Math.round(days)} days`;
};

// 78 -> "1 min 18 s", 45 -> "45 s"
export const formatDuration = (seconds) => {
  const total = Number(seconds) || 0;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes === 0) return `${rest} s`;
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
};
